import React, { useState, useEffect, useMemo, useRef } from "react";
import { apiFetch, fmt, can } from "../api";

// ===================== TABLE EDITOR PANEL (Chunk SA-C, 2026-05-02) =====================
// Generic browse-any-table surface for the System Admin compartment.
//
// Read-only mode — Chunk SA-C ships browse + sort + global search +
// per-column filter + filtered aggregates + opt-in FK label resolution.
// Edit mode lands in Chunk SA-D with audit_log write-through.
//
// Backend: /sysadmin/tables (list), /sysadmin/tables/{name} (schema),
// POST /sysadmin/tables/{name}/rows (page query). All gated on
// view_sysadmin_panel server-side; this component is also gated on the
// same cap at the parent (SystemAdmin.js).
//
// Key UX choices:
//   - Picker top-left lists all visible tables with row-count estimate.
//   - Click a table → schema + first page load.
//   - Each column header click cycles ASC → DESC → none.
//   - Per-column filter inputs sit below each header (text only for now;
//     SA-D will add type-aware operators).
//   - Single global search box above the grid composes AND with column filters.
//   - "Show FK labels" toggle (default off) — when on, FK cells render as
//     "47 (Jane Smith)" using batched backend lookups.
//   - Footer shows filtered_count + (if applicable) SUM totals per money col.
//   - Tables over 100k rows skip auto-aggregates; user can click "compute" to force.

export default function TableEditor() {
  const [tables, setTables]       = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [active, setActive]       = useState(null);   // {name, row_count_estimate, edit_blocked}
  const [schema, setSchema]       = useState(null);   // {columns, foreign_keys, sum_columns, ...}
  const [page, setPage]           = useState(0);
  const [pageSize]                = useState(50);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDir, setSortDir]     = useState("ASC");
  const [search, setSearch]       = useState("");
  const [colFilters, setColFilters] = useState({}); // {col_name: value}
  const [resolveFk, setResolveFk] = useState(false);
  const [forceAgg, setForceAgg]   = useState(false);
  const [data, setData]           = useState(null);  // {rows, filtered_count, aggregates, fk_labels, ...}
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const debounceRef = useRef(null);

  // ===== SA-D: edit-mode state =====
  // WS0-A / LD-REV-001 (2026-09-10): raw table writes are system_admin only.
  // The write endpoints require `edit_sysadmin_tables`, NOT the broader
  // `edit_sysadmin_panel` (which still gates System Settings / custom lists /
  // branding). This is the display half only — the backend is the gate.
  const canEdit = can("edit_sysadmin_tables");
  // editingRow: null = closed, "new" = insert modal, dict = edit modal for that row.
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm]     = useState({}); // form values keyed by column name
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState(null);

  // Initial: load table list.
  useEffect(() => {
    apiFetch("/sysadmin/tables")
      .then(d => { setTables(d); setTablesLoading(false); })
      .catch(e => { setError(String(e)); setTablesLoading(false); });
  }, []);

  // When user picks a table: load schema, reset query state, fetch page 1.
  const pickTable = (t) => {
    setActive(t);
    setSchema(null);
    setPage(0);
    setSortColumn(null);
    setSortDir("ASC");
    setSearch("");
    setColFilters({});
    setResolveFk(false);
    setForceAgg(false);
    setData(null);
    setError(null);
    apiFetch(`/sysadmin/tables/${t.name}`)
      .then(s => setSchema(s))
      .catch(e => setError(String(e)));
  };

  // Build the filters array from the colFilters map.
  const buildFilters = () => Object.entries(colFilters)
    .filter(([_, v]) => v && v.trim())
    .map(([col, val]) => ({ column: col, op: "ILIKE", value: val }));

  // Fetch a page using current sort/search/filter state.
  const fetchPage = (opts = {}) => {
    if (!active) return;
    setLoading(true);
    setError(null);
    const body = {
      page: opts.page ?? page,
      page_size: pageSize,
      sort_column: opts.sortColumn ?? sortColumn,
      sort_dir: opts.sortDir ?? sortDir,
      search: (opts.search ?? search) || null,
      filters: opts.filters ?? buildFilters(),
      resolve_fk: opts.resolveFk ?? resolveFk,
      force_aggregates: opts.forceAgg ?? forceAgg,
    };
    apiFetch(`/sysadmin/tables/${active.name}/rows`, { method: "POST", body })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  };

  // Trigger fetch on table-pick once schema loads, and on page changes.
  useEffect(() => {
    if (active && schema) fetchPage({ page });
  }, [active?.name, schema, page]);

  // Debounced fetch on search / filter changes (resets to page 0).
  useEffect(() => {
    if (!active || !schema) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchPage({ page: 0 });
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, colFilters]);

  // Header click cycles sort: none → ASC → DESC → none.
  const cycleSort = (colName) => {
    let nextCol = colName, nextDir = "ASC";
    if (sortColumn === colName) {
      if (sortDir === "ASC") nextDir = "DESC";
      else { nextCol = null; nextDir = "ASC"; }
    }
    setSortColumn(nextCol);
    setSortDir(nextDir);
    fetchPage({ sortColumn: nextCol, sortDir: nextDir, page: 0 });
    setPage(0);
  };

  // Toggle FK resolution — refetch with new flag.
  const toggleFk = () => {
    const v = !resolveFk;
    setResolveFk(v);
    fetchPage({ resolveFk: v });
  };

  // Manually compute aggregates on a >100k table.
  const computeAggregates = () => {
    setForceAgg(true);
    fetchPage({ forceAgg: true });
  };

  // Render a single cell. If FK + resolveFk on + label available, show "id (label)".
  const renderCell = (row, colName) => {
    const v = row[colName];
    if (v === null || v === undefined) return <span style={{color:"#9ca3af"}}>—</span>;
    const fkMap = data?.fk_labels?.[colName];
    if (fkMap && fkMap[v] != null) {
      return <span>{String(v)} <span style={{color:"#6b7280"}}>({fkMap[v]})</span></span>;
    }
    if (typeof v === "object") return <span style={{fontFamily:"monospace",fontSize:11}}>{JSON.stringify(v)}</span>;
    if (typeof v === "boolean") return v ? "✓" : "—";
    const s = String(v);
    if (s.length > 80) return <span title={s}>{s.slice(0,80)}…</span>;
    return s;
  };

  // ===== SA-D: edit handlers =====

  // Whether the editor can write to this table at all.
  const tableEditable = canEdit && schema && schema.pk_is_integer && !schema.edit_blocked_table;

  const openNewRow = () => {
    if (!tableEditable) return;
    const blank = {};
    schema.columns.forEach(c => {
      if (c.name === schema.pk_column) return; // PK assigned by sequence
      if (c.edit_blocked) return;
      blank[c.name] = c.default !== null ? "" : (c.nullable ? "" : "");
    });
    setEditForm(blank);
    setEditingRow("new");
    setEditError(null);
  };

  const openEditRow = (row) => {
    if (!tableEditable) return;
    const form = {};
    schema.columns.forEach(c => {
      const v = row[c.name];
      if (v === null || v === undefined) form[c.name] = "";
      else if (typeof v === "object") form[c.name] = JSON.stringify(v);
      else if (typeof v === "boolean") form[c.name] = v ? "true" : "false";
      else form[c.name] = String(v);
    });
    setEditForm(form);
    setEditingRow(row);
    setEditError(null);
  };

  const closeEdit = () => {
    setEditingRow(null);
    setEditForm({});
    setEditError(null);
    setEditSaving(false);
  };

  const saveEdit = () => {
    if (!tableEditable) return;
    setEditSaving(true);
    setEditError(null);
    const isInsert = editingRow === "new";
    // Build values object — skip PK on insert (sequence-assigned), skip edit-blocked.
    const values = {};
    schema.columns.forEach(c => {
      if (c.edit_blocked) return;
      if (c.name === schema.pk_column) return;
      values[c.name] = editForm[c.name];
    });
    const url = isInsert
      ? `/sysadmin/tables/${active.name}/row`
      : `/sysadmin/tables/${active.name}/row/${editingRow[schema.pk_column]}`;
    const method = isInsert ? "POST" : "PATCH";
    apiFetch(url, { method, body: { values } })
      .then(() => {
        closeEdit();
        fetchPage();
      })
      .catch(e => {
        setEditSaving(false);
        // The thrown text from apiFetch is the response body for non-OK.
        let msg = String(e).replace(/^Error:\s*/, "");
        try {
          const parsed = JSON.parse(msg);
          if (parsed.detail) msg = parsed.detail;
        } catch {}
        setEditError(msg);
      });
  };

  // WS0-A / H2: the delete confirm names the table, the id and every child
  // table the cascade would take with it. The old one-line window.confirm
  // named nothing, and a `claims` row has 25 ON DELETE CASCADE children --
  // one click destroyed diary, time, payments, reserves, documents and tasks
  // in a single statement. `claims` is now denied outright and the money spine
  // refuses with 409; this path is what remains, for ordinary tables.
  const deleteRow = (row) => {
    if (!tableEditable || schema?.delete_blocked) return;
    const pkVal = row[schema.pk_column];
    setError(null);
    apiFetch(`/sysadmin/tables/${active.name}/row/${pkVal}/dependents`)
      .then(dep => {
        if (dep.delete_blocked) {
          setError(dep.delete_blocked_reason || "Deletion is blocked on this table.");
          return;
        }
        const deps = dep.dependents || [];
        const lines = deps.map(d =>
          `  \u2022 ${d.row_count} row(s) in ${d.table}.${d.column} \u2014 ON DELETE ${d.on_delete}`
        );
        const total = dep.dependent_row_total || 0;
        let msg = `Delete ${active.name} row where ${schema.pk_column} = ${pkVal}?`;
        if (total > 0) {
          msg += `\n\nThis will also affect ${total} dependent row(s):\n${lines.join("\n")}`;
          msg += `\n\nEvery affected row is snapshotted into the audit log first, `
               + `but there is no restore button. Type-check the id above before continuing.`;
        } else {
          msg += `\n\nNo dependent rows found.`;
        }
        msg += `\n\nRecorded in the audit log as a DELETE by you.`;
        if (!window.confirm(msg)) return;
        const q = total > 0 ? "?cascade_ack=true" : "";
        return apiFetch(`/sysadmin/tables/${active.name}/row/${pkVal}${q}`, { method: "DELETE" })
          .then(() => fetchPage());
      })
      .catch(e => {
        let msg = String(e).replace(/^Error:\s*/, "");
        try {
          const parsed = JSON.parse(msg);
          if (parsed.detail) msg = parsed.detail;
        } catch {}
        setError(`Delete failed: ${msg}`);
      });
  };

  // ---------- LEFT COLUMN: TABLE PICKER ----------
  const renderPicker = () => (
    <div style={{width:240, borderRight:"1px solid #e5e7eb", paddingRight:12, maxHeight:"75vh", overflowY:"auto"}}>
      <div style={{fontSize:12, fontWeight:600, color:"#374151", marginBottom:6}}>Tables ({tables.length})</div>
      {tablesLoading && <div style={{fontSize:12, color:"#6b7280"}}>Loading…</div>}
      {!tablesLoading && tables.map(t => (
        <button key={t.name}
                onClick={() => pickTable(t)}
                style={{
                  display:"block", width:"100%", textAlign:"left",
                  padding:"4px 8px", marginBottom:2, fontSize:12,
                  background: active?.name === t.name ? "#1e3a8a" : "transparent",
                  color:    active?.name === t.name ? "#fff"     : "#1f2937",
                  border:"none", borderRadius:3, cursor:"pointer",
                }}>
          <div style={{fontWeight:500}}>
            {t.name}
            {t.edit_blocked && <span title="Read-only" style={{marginLeft:4,fontSize:10}}>🔒</span>}
          </div>
          <div style={{fontSize:10, opacity:0.7}}>~{t.row_count_estimate.toLocaleString()} rows</div>
        </button>
      ))}
    </div>
  );

  // ---------- TOP TOOLBAR ----------
  const renderToolbar = () => (
    <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:8, flexWrap:"wrap"}}>
      <input
        type="text"
        placeholder="Search all columns…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{flex:"1 1 240px", maxWidth:360, padding:"4px 8px", fontSize:13, border:"1px solid #d1d5db", borderRadius:3}}
      />
      <label style={{fontSize:12, display:"flex", alignItems:"center", gap:4, cursor:"pointer"}}>
        <input type="checkbox" checked={resolveFk} onChange={toggleFk}/>
        Show FK labels
      </label>
      {tableEditable && (
        <button className="btn btn-sm btn-primary" onClick={openNewRow}>
          + New row
        </button>
      )}
      {data?.aggregate_skipped_reason === "table_too_large" && (
        <button className="btn btn-sm btn-outline" onClick={computeAggregates}>
          Compute totals (table &gt;100k rows)
        </button>
      )}
      <div style={{flex:1}}/>
      {data && (
        <span style={{fontSize:12, color:"#6b7280"}}>
          {data.filtered_count.toLocaleString()} matching · ~{data.row_count_estimate.toLocaleString()} total
          {data.hidden_by_wall > 0 && (
            <span title="Rows on TPA matters you may not see, or on voided claims. The IA/TPA wall applies here exactly as it does everywhere else."
                  style={{marginLeft:8, color:"#b45309"}}>
              · {data.hidden_by_wall.toLocaleString()} hidden by the IA/TPA wall
            </span>
          )}
          {data.phi_access_logged && (
            <span title="This table carries a claim_id, so this read was recorded in phi_access_log."
                  style={{marginLeft:8, color:"#6b7280"}}>
              · PHI read logged
            </span>
          )}
        </span>
      )}
    </div>
  );

  // ---------- DATA GRID ----------
  const renderGrid = () => {
    if (!schema) return <div style={{padding:20, color:"#6b7280", fontSize:13}}>Loading schema…</div>;
    if (loading && !data) return <div style={{padding:20, color:"#6b7280", fontSize:13}}>Loading rows…</div>;
    const cols = schema.columns;
    const sumCols = new Set(schema.sum_columns || []);
    return (
      <div style={{overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:3}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
          <thead>
            <tr style={{background:"#f3f4f6"}}>
              {cols.map(c => (
                <th key={c.name}
                    onClick={() => cycleSort(c.name)}
                    style={{
                      padding:"6px 8px", textAlign:"left", borderBottom:"1px solid #e5e7eb",
                      cursor:"pointer", whiteSpace:"nowrap", userSelect:"none",
                      background: sortColumn === c.name ? "#dbeafe" : undefined,
                    }}>
                  {c.name}
                  {sortColumn === c.name && (
                    <span style={{marginLeft:4, fontSize:10}}>{sortDir === "ASC" ? "▲" : "▼"}</span>
                  )}
                  {sumCols.has(c.name) && (
                    <span title="Summed in totals" style={{marginLeft:4, fontSize:10, color:"#059669"}}>Σ</span>
                  )}
                </th>
              ))}
              {tableEditable && (
                <th style={{padding:"6px 8px", textAlign:"left", borderBottom:"1px solid #e5e7eb", fontSize:11, color:"#6b7280"}}>Actions</th>
              )}
            </tr>
            <tr style={{background:"#fafafa"}}>
              {cols.map(c => (
                <td key={c.name} style={{padding:"2px 4px", borderBottom:"1px solid #e5e7eb"}}>
                  <input
                    type="text"
                    value={colFilters[c.name] || ""}
                    onChange={e => setColFilters(prev => ({...prev, [c.name]: e.target.value}))}
                    placeholder="filter…"
                    style={{width:"100%", padding:"2px 4px", fontSize:11, border:"1px solid #d1d5db", borderRadius:2}}
                  />
                </td>
              ))}
              {tableEditable && (
                <td style={{padding:"2px 4px", borderBottom:"1px solid #e5e7eb"}}/>
              )}
            </tr>
          </thead>
          <tbody>
            {data?.rows?.length === 0 && (
              <tr><td colSpan={cols.length + (tableEditable ? 1 : 0)} style={{padding:20, textAlign:"center", color:"#6b7280"}}>No rows match.</td></tr>
            )}
            {data?.rows?.map((row, idx) => (
              <tr key={idx} style={{borderBottom:"1px solid #f3f4f6"}}>
                {cols.map(c => (
                  <td key={c.name} style={{padding:"4px 8px", whiteSpace:"nowrap", maxWidth:300, overflow:"hidden", textOverflow:"ellipsis"}}>
                    {renderCell(row, c.name)}
                  </td>
                ))}
                {tableEditable && (
                  <td style={{padding:"4px 8px", whiteSpace:"nowrap"}}>
                    <button onClick={() => openEditRow(row)}
                            title="Edit"
                            style={{padding:"2px 6px", fontSize:11, marginRight:4, cursor:"pointer", background:"#f3f4f6", border:"1px solid #d1d5db", borderRadius:2}}>
                      ✎
                    </button>
                    {schema?.delete_blocked ? (
                      <span title={schema.delete_blocked_reason}
                            style={{padding:"2px 6px", fontSize:11, color:"#9ca3af", border:"1px solid #e5e7eb", borderRadius:2, cursor:"not-allowed"}}>
                        🔒
                      </span>
                    ) : (
                      <button onClick={() => deleteRow(row)}
                              title="Delete"
                              style={{padding:"2px 6px", fontSize:11, cursor:"pointer", background:"#fef2f2", border:"1px solid #fca5a5", color:"#991b1b", borderRadius:2}}>
                        🗑
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {data?.aggregates && (
            <tfoot>
              <tr style={{background:"#f9fafb", fontWeight:600}}>
                {cols.map(c => (
                  <td key={c.name} style={{padding:"6px 8px", borderTop:"2px solid #e5e7eb", fontSize:11}}>
                    {data.aggregates[c.name] != null && (
                      <span style={{color:"#059669"}}>Σ {Number(data.aggregates[c.name]).toLocaleString()}</span>
                    )}
                  </td>
                ))}
                {tableEditable && (
                  <td style={{padding:"6px 8px", borderTop:"2px solid #e5e7eb"}}/>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  // ---------- PAGINATION ----------
  const renderPagination = () => {
    if (!data) return null;
    const totalPages = Math.max(1, Math.ceil(data.filtered_count / pageSize));
    return (
      <div style={{display:"flex", gap:8, alignItems:"center", marginTop:8, fontSize:12}}>
        <button className="btn btn-sm btn-outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p-1))}>← Prev</button>
        <span>Page {page+1} of {totalPages.toLocaleString()}</span>
        <button className="btn btn-sm btn-outline" disabled={page >= totalPages-1} onClick={() => setPage(p => p+1)}>Next →</button>
        {loading && <span style={{color:"#6b7280"}}>loading…</span>}
      </div>
    );
  };

  // ---------- EDIT/INSERT MODAL (SA-D) ----------
  const renderEditModal = () => {
    if (editingRow === null) return null;
    const isInsert = editingRow === "new";
    return (
      <div style={{
        position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000,
        display:"flex", alignItems:"flex-start", justifyContent:"center",
        paddingTop:60, paddingBottom:40, overflowY:"auto",
      }} onClick={closeEdit}>
        <div onClick={e => e.stopPropagation()}
             style={{
               background:"#fff", borderRadius:6, padding:20, minWidth:520,
               maxWidth:720, width:"90%", boxShadow:"0 8px 32px rgba(0,0,0,0.2)",
             }}>
          <div style={{display:"flex", alignItems:"baseline", gap:8, marginBottom:14}}>
            <h3 style={{margin:0, fontSize:16, color:"#1f2937"}}>
              {isInsert ? `New row in ${active.name}` : `Edit ${active.name} row #${editingRow[schema.pk_column]}`}
            </h3>
          </div>
          <div style={{maxHeight:"60vh", overflowY:"auto", paddingRight:6}}>
            {schema.columns.map(c => {
              const isPk = c.name === schema.pk_column;
              const readOnly = c.edit_blocked || (isPk && !isInsert);
              const isPkOnInsert = isPk && isInsert;
              return (
                <div key={c.name} style={{marginBottom:10}}>
                  <label style={{display:"block", fontSize:12, color:"#374151", marginBottom:2, fontWeight:500}}>
                    {c.name}
                    <span style={{marginLeft:6, fontSize:10, color:"#9ca3af", fontWeight:400}}>
                      {c.data_type}{c.nullable ? "" : " · NOT NULL"}{isPk ? " · PK" : ""}
                    </span>
                    {c.edit_blocked && <span style={{marginLeft:6, fontSize:10, color:"#dc2626"}}>🔒 read-only</span>}
                  </label>
                  {isPkOnInsert ? (
                    <input
                      type="text" disabled value="(auto-assigned)"
                      style={{width:"100%", padding:"4px 6px", fontSize:12, border:"1px solid #e5e7eb", borderRadius:2, background:"#f9fafb", color:"#9ca3af"}}
                    />
                  ) : c.data_type === "boolean" ? (
                    <select
                      disabled={readOnly}
                      value={editForm[c.name] || ""}
                      onChange={e => setEditForm(p => ({...p, [c.name]: e.target.value}))}
                      style={{width:"100%", padding:"4px 6px", fontSize:12, border:"1px solid #d1d5db", borderRadius:2, background: readOnly ? "#f9fafb" : "#fff"}}
                    >
                      <option value="">{c.nullable ? "(null)" : "—"}</option>
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  ) : (c.data_type === "jsonb" || c.data_type === "json" || c.data_type === "text") ? (
                    <textarea
                      readOnly={readOnly}
                      rows={c.data_type === "jsonb" || c.data_type === "json" ? 4 : 2}
                      value={editForm[c.name] || ""}
                      onChange={e => setEditForm(p => ({...p, [c.name]: e.target.value}))}
                      style={{width:"100%", padding:"4px 6px", fontSize:12, fontFamily: c.data_type.includes("json") ? "monospace" : undefined, border:"1px solid #d1d5db", borderRadius:2, background: readOnly ? "#f9fafb" : "#fff", resize:"vertical"}}
                    />
                  ) : (
                    <input
                      type="text"
                      readOnly={readOnly}
                      value={editForm[c.name] || ""}
                      onChange={e => setEditForm(p => ({...p, [c.name]: e.target.value}))}
                      placeholder={c.nullable ? "(leave blank for null)" : ""}
                      style={{width:"100%", padding:"4px 6px", fontSize:12, border:"1px solid #d1d5db", borderRadius:2, background: readOnly ? "#f9fafb" : "#fff"}}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {editError && (
            <div style={{padding:8, background:"#fef2f2", color:"#991b1b", border:"1px solid #fecaca", borderRadius:3, marginTop:10, fontSize:12}}>
              {editError}
            </div>
          )}
          <div style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:14, paddingTop:12, borderTop:"1px solid #e5e7eb"}}>
            <button className="btn btn-sm btn-outline" onClick={closeEdit} disabled={editSaving}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={saveEdit} disabled={editSaving}>
              {editSaving ? "Saving…" : isInsert ? "Create row" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- LAYOUT ----------
  return (
    <div>
      {error && (
        <div style={{padding:8, background:"#fef2f2", color:"#991b1b", border:"1px solid #fecaca", borderRadius:3, marginBottom:8, fontSize:12}}>
          {error}
        </div>
      )}
      <div style={{display:"flex", gap:12}}>
        {renderPicker()}
        <div style={{flex:1, minWidth:0}}>
          {!active && (
            <div style={{padding:40, color:"#6b7280", fontSize:13, textAlign:"center"}}>
              Pick a table from the left to begin.
            </div>
          )}
          {active && (
            <>
              <div style={{display:"flex", alignItems:"baseline", gap:8, marginBottom:8}}>
                <h3 style={{margin:0, fontSize:16, color:"#1f2937"}}>{active.name}</h3>
                {active.edit_blocked && (
                  <span style={{fontSize:11, color:"#dc2626", background:"#fef2f2", padding:"1px 6px", borderRadius:2}}>
                    🔒 read-only
                  </span>
                )}
              </div>
              {renderToolbar()}
              {renderGrid()}
              {renderPagination()}
            </>
          )}
        </div>
      </div>
      {renderEditModal()}
    </div>
  );
}
